import React, { useEffect, useState } from "react";
import { teacherApi } from "./teacherApi";
import type { TeacherBatch } from "./types";

// const NODE_API = "http://localhost:9090/api";
const NODE_API = "https://node-api.mediitacademy.com/api";

export default function TeacherTests() {
  const [batches, setBatches] = useState<TeacherBatch[]>([]);
  const [tests, setTests] = useState<any[]>([]);
  const [selectedTestId, setSelectedTestId] = useState<number | null>(null);
  const [selectedTestTitle, setSelectedTestTitle] = useState<string>("");

  // Test Creation Form
  const [testForm, setTestForm] = useState({
    title: "",
    batchId: "",
    totalMarks: 100,
    durationMinutes: 60,
    startTime: "",
    endTime: "",
  });

  // Manual Question Form
  const [qForm, setQForm] = useState({
    questionText: "",
    optionA: "",
    optionB: "",
    optionC: "",
    optionD: "",
    correctOption: "A",
    explanation: "",
  });

  // Bulk CSV file state
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    teacherApi.myBatches().then(setBatches);
    loadTests();
  }, []);

  const loadTests = async () => {
    try {
      const res = await fetch(`${NODE_API}/tests`);
      if (res.ok) {
        const data = await res.json();
        setTests(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreateTest = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch(`${NODE_API}/tests/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(testForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      alert("Test initialized successfully!");
      setTestForm({ title: "", batchId: "", totalMarks: 100, durationMinutes: 60, startTime: "", endTime: "" });
      setSelectedTestId(data.testId);
      loadTests();
    } catch (err: any) {
      alert(err.message || "Failed to create test");
    }
  };

  const handleAddQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTestId) return;

    try {
      const res = await fetch(`${NODE_API}/tests/${selectedTestId}/question`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(qForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to add question");

      alert("Question added successfully!");
      setQForm({ questionText: "", optionA: "", optionB: "", optionC: "", optionD: "", correctOption: "A", explanation: "" });
    } catch (err: any) {
      alert(err.message);
    }
  };

  // Bulk CSV Question Upload Parser
  const handleBulkUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTestId || !csvFile) {
      alert("Please select a test and choose a CSV file.");
      return;
    }

    setUploading(true);
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target?.result as string;
        const lines = text.split("\n").filter((l) => l.trim().length > 0);
        
        // Assume CSV Format: questionText,optionA,optionB,optionC,optionD,correctOption(A/B/C/D),explanation
        let count = 0;
        for (let i = 1; i < lines.length; i++) { // Skip header
          const cols = lines[i].split(",").map((c) => c.trim().replace(/^"|"$/g, ''));
          if (cols.length >= 6) {
            await fetch(`${NODE_API}/tests/${selectedTestId}/question`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                questionText: cols[0],
                optionA: cols[1],
                optionB: cols[2],
                optionC: cols[3],
                optionD: cols[4],
                correctOption: cols[5],
                explanation: cols[6] || "",
              }),
            });
            count++;
          }
        }
        alert(`Successfully imported ${count} questions via CSV!`);
        setCsvFile(null);
      } catch (err) {
        alert("Failed to parse or upload CSV file.");
      } finally {
        setUploading(false);
      }
    };
    reader.readAsText(csvFile);
  };

  return (
    <div className="space-y-6 max-w-6xl font-['Inter']">
      <header className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-xs font-black uppercase tracking-wide text-blue-600">Test Engine</p>
        <h2 className="mt-2 text-3xl font-black text-slate-950">Create & Manage Batch Tests</h2>
        <p className="mt-2 text-sm font-bold text-slate-500">
          Design timed mock tests, add questions manually, or upload via CSV.
        </p>
      </header>

      <div className="grid gap-6 xl:grid-cols-2">
        {/* 1. CREATE TEST */}
        <div className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-xl font-black text-slate-950 mb-4">1. Initialize New Test</h3>
          <form onSubmit={handleCreateTest} className="space-y-4">
            <div>
              <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">Test Title</label>
              <input
                value={testForm.title}
                onChange={(e) => setTestForm({ ...testForm, title: e.target.value })}
                placeholder="e.g. Physics Grand Mock Test"
                className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold outline-none"
                required
              />
            </div>

            <div>
              <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">Target Batch</label>
              <select
                value={testForm.batchId}
                onChange={(e) => setTestForm({ ...testForm, batchId: e.target.value })}
                className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold outline-none"
                required
              >
                <option value="">-- Choose Batch --</option>
                {batches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name} ({b.courseTitle})</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">Total Marks</label>
                <input type="number" value={testForm.totalMarks} onChange={(e) => setTestForm({ ...testForm, totalMarks: Number(e.target.value) })} className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold outline-none" required />
              </div>
              <div>
                <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">Duration (Mins)</label>
                <input type="number" value={testForm.durationMinutes} onChange={(e) => setTestForm({ ...testForm, durationMinutes: Number(e.target.value) })} className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold outline-none" required />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">Start Time</label>
                <input type="datetime-local" value={testForm.startTime} onChange={(e) => setTestForm({ ...testForm, startTime: e.target.value })} className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 text-xs font-bold outline-none" required />
              </div>
              <div>
                <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">End Time</label>
                <input type="datetime-local" value={testForm.endTime} onChange={(e) => setTestForm({ ...testForm, endTime: e.target.value })} className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 text-xs font-bold outline-none" required />
              </div>
            </div>

            <button type="submit" className="w-full rounded-2xl bg-blue-600 px-5 py-4 text-sm font-black text-white hover:bg-blue-700 shadow-lg shadow-blue-600/20 transition">
              Create Test 🚀
            </button>
          </form>
        </div>

        {/* 2. ADD QUESTIONS (MANUAL + BULK CSV UPLOAD) */}
        <div className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-black text-slate-950">2. Add Questions</h3>
            {selectedTestId ? (
              <span className="px-3 py-1 bg-emerald-100 text-emerald-800 text-xs font-black rounded-full">
                Active Test ID: #{selectedTestId}
              </span>
            ) : (
              <span className="px-3 py-1 bg-amber-100 text-amber-800 text-xs font-black rounded-full">
                Select Test Below 👇
              </span>
            )}
          </div>

          {!selectedTestId ? (
            <div className="py-28 text-center border border-dashed border-slate-200 rounded-2xl bg-slate-50">
              <p className="text-sm font-bold text-slate-500">Please select any test from the list below to add questions.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* MANUAL FORM */}
              <form onSubmit={handleAddQuestion} className="space-y-3 p-4 rounded-2xl bg-slate-50 border border-slate-200">
                <p className="text-xs font-black text-slate-900 uppercase">Manual Entry</p>
                <textarea
                  value={qForm.questionText}
                  onChange={(e) => setQForm({ ...qForm, questionText: e.target.value })}
                  rows={2}
                  placeholder="Question statement..."
                  className="w-full rounded-xl border border-slate-200 bg-white p-3 text-xs font-bold outline-none"
                  required
                />
                <div className="grid grid-cols-2 gap-2">
                  <input value={qForm.optionA} onChange={(e) => setQForm({ ...qForm, optionA: e.target.value })} placeholder="Option A" className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-xs font-bold outline-none" required />
                  <input value={qForm.optionB} onChange={(e) => setQForm({ ...qForm, optionB: e.target.value })} placeholder="Option B" className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-xs font-bold outline-none" required />
                  <input value={qForm.optionC} onChange={(e) => setQForm({ ...qForm, optionC: e.target.value })} placeholder="Option C" className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-xs font-bold outline-none" required />
                  <input value={qForm.optionD} onChange={(e) => setQForm({ ...qForm, optionD: e.target.value })} placeholder="Option D" className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-xs font-bold outline-none" required />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <select value={qForm.correctOption} onChange={(e) => setQForm({ ...qForm, correctOption: e.target.value })} className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-xs font-bold">
                    <option value="A">Correct: A</option>
                    <option value="B">Correct: B</option>
                    <option value="C">Correct: C</option>
                    <option value="D">Correct: D</option>
                  </select>
                  <input value={qForm.explanation} onChange={(e) => setQForm({ ...qForm, explanation: e.target.value })} placeholder="Explanation" className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-xs font-bold outline-none" />
                </div>
                <button type="submit" className="w-full py-2.5 bg-slate-900 text-white text-xs font-black rounded-xl hover:bg-slate-800">
                  + Add Question Manually
                </button>
              </form>

              {/* BULK CSV UPLOAD */}
              <form onSubmit={handleBulkUpload} className="p-4 rounded-2xl bg-blue-50/50 border border-blue-100 space-y-3">
                <p className="text-xs font-black text-blue-900 uppercase">Bulk Upload (CSV Format)</p>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                  className="w-full text-xs text-slate-500 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-black file:bg-blue-600 file:text-white"
                  required
                />
                <button type="submit" disabled={uploading} className="w-full py-2.5 bg-blue-600 text-white text-xs font-black rounded-xl hover:bg-blue-700 disabled:bg-slate-300">
                  {uploading ? "Importing..." : "Upload CSV & Import Bulk Questions 📂"}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>

      {/* ALL CREATED TESTS LIST */}
      <div className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="text-xl font-black text-slate-950 mb-4">All Created Tests (Select to Add Questions)</h3>
        <div className="grid md:grid-cols-3 gap-4">
          {tests.map((t) => (
            <div key={t.id} className={`p-4 rounded-2xl border transition ${selectedTestId === t.id ? 'border-blue-500 bg-blue-50/40 shadow-sm' : 'border-slate-100 bg-[#f8fbff]'}`}>
              <div className="flex justify-between items-center mb-1">
                <span className="text-[10px] font-black uppercase bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{t.batchName}</span>
                <span className="text-[10px] font-black text-slate-400">ID: #{t.id}</span>
              </div>
              <h4 className="text-base font-black text-slate-900 mt-1">{t.title}</h4>
              <p className="text-xs font-bold text-slate-500 mb-4">Marks: {t.total_marks} • Time: {t.duration_minutes}m</p>
              
              <button 
                onClick={() => {
                  setSelectedTestId(t.id);
                  setSelectedTestTitle(t.title);
                }}
                className={`w-full py-2.5 text-xs font-black rounded-xl transition ${selectedTestId === t.id ? 'bg-emerald-600 text-white shadow-md' : 'bg-slate-900 text-white hover:bg-slate-800'}`}
              >
                {selectedTestId === t.id ? '✓ Active for Questions' : 'Select Test 🎯'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}